/**
 * Save-flow confirmation hook for the LocationPicker.
 *
 * When the picker is in "Editing" mode (locationId set — save will
 * mutate the linked doc), the parent form awaits this hook before
 * persisting. The hook decides whether to show a confirmation dialog:
 *
 *   - locationId set      → show dialog [Update] / [Save as new copy] / [Cancel]
 *   - locationId null     → no dialog, intent already explicit (organizer
 *                            either explicitly toggled to "New copy" via
 *                            the picker's button, or this is a fresh
 *                            authoring session)
 *   - value === null      → no dialog, no-op
 *
 * After the user chooses, the hook calls `resolveLocationPickerValue`
 * with the appropriate locationId override and returns the resolved
 * id (or `undefined` to signal cancellation, distinct from `null` /
 * "empty value").
 */

import { useState, useCallback, type ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import {
  resolveLocationPickerValue,
} from '../../services/eventLocationService';
import type { LocationPickerValue } from './LocationPicker';

interface PendingState {
  value: NonNullable<LocationPickerValue>;
  eventId: string;
  resolve: (locationId: string | null | undefined) => void;
}

export type ConfirmedLocationId = string | null | undefined;

export function useLocationSaveConfirm(): {
  /**
   * Resolve a picker value to a concrete locationId, prompting the
   * organizer first if save would mutate a shared linked location.
   *
   * Returns:
   *   - string  → created or updated location's id
   *   - null    → empty value, no doc was written
   *   - undefined → organizer cancelled the dialog; abort form save
   */
  confirmAndResolve: (
    value: LocationPickerValue,
    eventId: string,
  ) => Promise<ConfirmedLocationId>;
  dialog: ReactNode;
} {
  const [pending, setPending] = useState<PendingState | null>(null);

  const confirmAndResolve = useCallback(
    async (
      value: LocationPickerValue,
      eventId: string,
    ): Promise<ConfirmedLocationId> => {
      if (!value || !value.locationId) {
        // No dialog needed — pure new authoring or explicit "New copy"
        // mode (user already chose this via the picker's toggle button).
        return resolveLocationPickerValue(value, eventId);
      }
      // Skip the dialog when the picker is still pointing at a verified,
      // unedited copy of the loaded doc. `verified` is the "in sync with
      // the saved data" signal: pickerValueFromLocation copies it from
      // the doc, applyManualEdit flips it to false on any user edit, and
      // a fresh MapKit pick re-sets it to true. So `verified === true`
      // here means "no divergence since hydrate" — saving is a no-op
      // against the linked doc and no confirmation is meaningful.
      // (Locations originally authored as "Custom" stay `verified: false`
      // and still prompt on save, which is the right behavior — those
      // are exactly the entries an organizer might want to re-verify.)
      if (value.verified) {
        return resolveLocationPickerValue(value, eventId);
      }
      // Editing mode with manual edits — prompt for confirmation.
      return new Promise<ConfirmedLocationId>((resolve) => {
        setPending({ value, eventId, resolve });
      });
    },
    [],
  );

  const close = (id: ConfirmedLocationId) => {
    if (!pending) return;
    pending.resolve(id);
    setPending(null);
  };

  async function handle(choice: 'update' | 'fork' | 'cancel') {
    if (!pending) return;
    if (choice === 'cancel') {
      close(undefined);
      return;
    }
    const valueForResolve =
      choice === 'fork'
        ? { ...pending.value, locationId: null }
        : pending.value;
    try {
      const id = await resolveLocationPickerValue(
        valueForResolve,
        pending.eventId,
      );
      close(id);
    } catch (err) {
      // Surface the error by rejecting the parent's await — easiest is
      // to throw from the dialog's onClose path. The parent's try/catch
      // around the form submit handles it.
      close(undefined);
      throw err;
    }
  }

  const dialog = (
    <Dialog
      open={!!pending}
      onClose={() => handle('cancel')}
      maxWidth="sm"
      fullWidth
      data-testid="location-save-confirm-dialog"
    >
      <DialogTitle>Save changes to existing location?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          You&apos;re editing{' '}
          <strong>{pending?.value.label || 'an existing location'}</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Updating the original applies your changes everywhere this
          location is used (the event venue, any sessions or travel
          items pointing at it). Saving as a new copy leaves the
          original alone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handle('cancel')}>Cancel</Button>
        <Button onClick={() => handle('fork')}>Save as new copy</Button>
        <Button onClick={() => handle('update')} variant="contained">
          Update original
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { confirmAndResolve, dialog };
}
